import React, { useState, useEffect, useMemo, useCallback } from 'react'
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Paper,
  Stack,
  Chip,
  LinearProgress,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import { useMutation } from '@apollo/client'
import CloseIcon from '@mui/icons-material/Close'
import EditIcon from '@mui/icons-material/Edit'
import SaveIcon from '@mui/icons-material/Save'
import CancelIcon from '@mui/icons-material/Cancel'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import InfoIcon from '@mui/icons-material/Info'
import WarningIcon from '@mui/icons-material/Warning'
import PersonIcon from '@mui/icons-material/Person'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import WcIcon from '@mui/icons-material/Wc'
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import DevicesIcon from '@mui/icons-material/Devices'
import FlagIcon from '@mui/icons-material/Flag'
import BadgeIcon from '@mui/icons-material/Badge'
import { useToast } from '../../contexts/ToastContext'
import { useMultiState } from '../../hooks'
import updateParticipantMutation from '../../graphql/mutation/updateParticipantById'
import ParticipantEditForm, { InfoField } from './ParticipantEditForm'

// Utility functions
const formatDate = (dateString) => {
  if (!dateString) return '-'
  try {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    })
  } catch {
    return '-'
  }
}

const formatDateTime = (dateString) => {
  if (!dateString) return '-'
  try {
    return new Date(dateString).toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return '-'
  }
}

// Sub-components
const ImageCard = React.memo(({ title, imageUrl, alt }) => {
  const [imageError, setImageError] = useState(false)
  const [imageLoading, setImageLoading] = useState(true)

  return (
    <Box sx={{ width: '100%' }}>
      <Typography
        variant="body2"
        sx={{
          fontWeight: 500,
          color: 'text.secondary',
          fontSize: { xs: '0.8125rem', sm: '0.875rem', md: '0.9375rem' },
          mb: { xs: 1, sm: 1.25, md: 1.5 },
          textAlign: 'center',
        }}>
        {title}
      </Typography>
      <Box
        sx={{
          position: 'relative',
          width: { xs: '100%', sm: '200px', md: '220px' },
          maxWidth: { xs: '100%', sm: '200px', md: '220px' },
          aspectRatio: '3/4',
          margin: '0 auto',
          borderRadius: 2,
          overflow: 'hidden',
          backgroundColor: (theme) =>
            theme.palette.mode === 'light' ? 'rgba(255, 255, 255, 1)' : 'rgba(30, 30, 30, 1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: (theme) =>
            `1px solid ${theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.12)' : 'rgba(255, 255, 255, 0.12)'}`,
          transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
          '&:hover': {
            transform: 'scale(1.02)',
            boxShadow: (theme) =>
              theme.palette.mode === 'light'
                ? '0 4px 12px rgba(0, 0, 0, 0.15)'
                : '0 4px 12px rgba(255, 255, 255, 0.1)',
          },
          cursor: imageUrl ? 'pointer' : 'default',
        }}>
        {imageUrl && !imageError ? (
          <>
            {imageLoading && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                <LinearProgress sx={{ width: '60%' }} />
              </Box>
            )}
            <Box
              component="img"
              src={imageUrl}
              alt={alt}
              onLoad={() => setImageLoading(false)}
              onError={() => {
                setImageError(true)
                setImageLoading(false)
              }}
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: imageLoading ? 'none' : 'block',
              }}
            />
          </>
        ) : (
          <PersonOutlineIcon
            sx={{
              fontSize: { xs: '3rem', sm: '4rem', md: '5rem' },
              color: 'text.disabled',
            }}
          />
        )}
      </Box>
    </Box>
  )
})

ImageCard.displayName = 'ImageCard'

const FaceMatchConfidenceBar = React.memo(({ confidence }) => {
  const confidenceValue = parseFloat(confidence) || 0

  return (
    <Box
      sx={{
        mt: { xs: 2, sm: 2.5, md: 3 },
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}>
      <Chip
        label={`Face Match Confidence: ${confidenceValue.toFixed(1)}%`}
        sx={{
          backgroundColor: (theme) =>
            theme.palette.mode === 'light' ? 'rgba(76, 175, 80, 0.1)' : 'rgba(76, 175, 80, 0.2)',
          color: (theme) =>
            theme.palette.mode === 'light' ? 'rgba(76, 175, 80, 0.9)' : 'rgba(129, 199, 132, 0.9)',
          fontWeight: 600,
          fontSize: { xs: '0.875rem', sm: '0.9375rem', md: '1rem' },
          height: { xs: 32, sm: 36, md: 40 },
          px: { xs: 1.5, sm: 2, md: 2.5 },
        }}
      />
    </Box>
  )
})

FaceMatchConfidenceBar.displayName = 'FaceMatchConfidenceBar'

const VerificationHistoryItem = React.memo(({ activity, theme }) => {
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  if (!activity) return null

  const getStatusColor = (status) => {
    const colors = {
      checkin: {
        light: 'rgba(76, 175, 80, 0.1)',
        dark: 'rgba(76, 175, 80, 0.2)',
        textLight: 'rgba(76, 175, 80, 0.9)',
        textDark: 'rgba(129, 199, 132, 0.9)',
      },
      pickup: {
        light: 'rgba(33, 150, 243, 0.1)',
        dark: 'rgba(33, 150, 243, 0.2)',
        textLight: 'rgba(33, 150, 243, 0.9)',
        textDark: 'rgba(144, 202, 249, 0.9)',
      },
    }
    const color = colors[status] || {}
    return {
      bg: theme.palette.mode === 'light' ? color.light || 'transparent' : color.dark || 'transparent',
      text:
        theme.palette.mode === 'light' ? color.textLight || 'text.secondary' : color.textDark || 'text.secondary',
    }
  }

  const statusColors = getStatusColor(activity?.status)

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 1.5, sm: 2, md: 2.5 },
        borderRadius: 2,
        backgroundColor: (theme) =>
          theme.palette.mode === 'light' ? 'rgba(249, 250, 251, 0.5)' : 'rgba(18, 18, 18, 0.5)',
        border: (theme) =>
          `1px solid ${theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.08)'}`,
      }}>
      {isMobile ? (
        <Stack spacing={1.5}>
          {activity.status && (
            <Box>
              <Stack direction="row" spacing={0.75} alignItems="center" mb={0.75}>
                <CheckCircleIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.secondary', fontSize: '0.75rem' }}>
                  Status
                </Typography>
              </Stack>
              <Chip
                label={activity.status}
                size="small"
                sx={{
                  backgroundColor: statusColors.bg,
                  color: statusColors.text,
                  fontWeight: 500,
                  fontSize: '0.8125rem',
                }}
              />
            </Box>
          )}
          {activity.field && (
            <Box>
              <Stack direction="row" spacing={0.75} alignItems="center" mb={0.75}>
                <WarningIcon sx={{ fontSize: '1rem', color: 'error.main' }} />
                <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.secondary', fontSize: '0.75rem' }}>
                  Field
                </Typography>
              </Stack>
              <Typography sx={{ fontSize: '0.8125rem', color: 'error.main', fontWeight: 500 }}>
                {activity.field}
              </Typography>
            </Box>
          )}
          {activity.device && (
            <Box>
              <Stack direction="row" spacing={0.75} alignItems="center" mb={0.75}>
                <DevicesIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.secondary', fontSize: '0.75rem' }}>
                  Device
                </Typography>
              </Stack>
              <Typography sx={{ fontSize: '0.8125rem', color: 'text.primary' }}>{activity.device}</Typography>
            </Box>
          )}
          {activity.timestamp && (
            <Box>
              <Stack direction="row" spacing={0.75} alignItems="center" mb={0.75}>
                <AccessTimeIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.secondary', fontSize: '0.75rem' }}>
                  Timestamp
                </Typography>
              </Stack>
              <Typography sx={{ fontSize: '0.8125rem', color: 'text.primary' }}>
                {formatDateTime(activity.timestamp)}
              </Typography>
            </Box>
          )}
        </Stack>
      ) : (
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={{ xs: 1.5, sm: 2, md: 2.5 }}
          sx={{ flexWrap: 'wrap' }}>
          {activity.status && (
            <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 1rem)', md: '1 1 calc(25% - 1.875rem)' } }}>
              <Stack direction="row" spacing={0.75} alignItems="center" mb={0.75}>
                <CheckCircleIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    color: 'text.secondary',
                    fontSize: { xs: '0.75rem', sm: '0.8125rem', md: '0.875rem' },
                  }}>
                  Status
                </Typography>
              </Stack>
              <Chip
                label={activity.status}
                size="small"
                sx={{
                  backgroundColor: statusColors.bg,
                  color: statusColors.text,
                  fontWeight: 500,
                  fontSize: { xs: '0.8125rem', sm: '0.875rem', md: '0.9375rem' },
                }}
              />
            </Box>
          )}
          {activity.field && (
            <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 1rem)', md: '1 1 calc(25% - 1.875rem)' } }}>
              <Stack direction="row" spacing={0.75} alignItems="center" mb={0.75}>
                <WarningIcon sx={{ fontSize: '1rem', color: 'error.main' }} />
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    color: 'text.secondary',
                    fontSize: { xs: '0.75rem', sm: '0.8125rem', md: '0.875rem' },
                  }}>
                  Field
                </Typography>
              </Stack>
              <Typography
                sx={{
                  fontSize: { xs: '0.8125rem', sm: '0.875rem', md: '0.9375rem' },
                  color: 'error.main',
                  fontWeight: 500,
                }}>
                {activity.field}
              </Typography>
            </Box>
          )}
          {activity.device && (
            <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 1rem)', md: '1 1 calc(25% - 1.875rem)' } }}>
              <Stack direction="row" spacing={0.75} alignItems="center" mb={0.75}>
                <DevicesIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    color: 'text.secondary',
                    fontSize: { xs: '0.75rem', sm: '0.8125rem', md: '0.875rem' },
                  }}>
                  Device
                </Typography>
              </Stack>
              <Typography
                sx={{
                  fontSize: { xs: '0.8125rem', sm: '0.875rem', md: '0.9375rem' },
                  color: 'text.primary',
                }}>
                {activity.device}
              </Typography>
            </Box>
          )}
          {activity.timestamp && (
            <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 1rem)', md: '1 1 calc(25% - 1.875rem)' } }}>
              <Stack direction="row" spacing={0.75} alignItems="center" mb={0.75}>
                <AccessTimeIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    color: 'text.secondary',
                    fontSize: { xs: '0.75rem', sm: '0.8125rem', md: '0.875rem' },
                  }}>
                  Timestamp
                </Typography>
              </Stack>
              <Typography
                sx={{
                  fontSize: { xs: '0.8125rem', sm: '0.875rem', md: '0.9375rem' },
                  color: 'text.primary',
                }}>
                {formatDateTime(activity.timestamp)}
              </Typography>
            </Box>
          )}
        </Stack>
      )}
    </Paper>
  )
})

VerificationHistoryItem.displayName = 'VerificationHistoryItem'

// Main Component
const ParticipantDetailsDialog = ({ open, onClose, participant, eventId, onUpdate }) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'))
  const { showSuccess, showError } = useToast()
  const [updateParticipant, { loading: updating }] = useMutation(updateParticipantMutation)

  const [isEditMode, setIsEditMode] = useState(false)

  const initialState = useMemo(
    () => ({
      firstname: participant?.firstname || '',
      lastname: participant?.lastname || '',
      ageCategory: participant?.ageCategory || '',
      bib: participant?.bib || '',
      displayName: participant?.displayName || '',
      gender: participant?.gender || '',
      row: participant?.row || '',
      status: participant?.status || '',
      nationality: participant?.nationality || '',
      nationalId: participant?.nationalId || '',
      options: participant?.options || {},
    }),
    [participant]
  )

  const [formState, setFormState] = useMultiState(initialState)

  // Update formState when participant changes and not in edit mode
  useEffect(() => {
    if (participant && !isEditMode) {
      const updatedState = {
        firstname: participant?.firstname || '',
        lastname: participant?.lastname || '',
        ageCategory: participant?.ageCategory || '',
        bib: participant?.bib || '',
        displayName: participant?.displayName || '',
        gender: participant?.gender || '',
        row: participant?.row || '',
        status: participant?.status || '',
        nationality: participant?.nationality || '',
        nationalId: participant?.nationalId || '',
        options: participant?.options || {},
      }
      setFormState(updatedState)
    }
  }, [participant, isEditMode, setFormState])

  const genderList = ['male', 'female']

  const fullName = useMemo(
    () => `${formState.firstname || ''} ${formState.lastname || ''}`.trim() || '-',
    [formState.firstname, formState.lastname]
  )

  const verificationHistory = useMemo(() => {
    return participant?.activities || []
  }, [participant?.activities])

  const handleEdit = useCallback(() => {
    setIsEditMode(true)
  }, [])

  const handleCancel = useCallback(() => {
    setIsEditMode(false)
    if (participant) {
      const resetState = {
        firstname: participant?.firstname || '',
        lastname: participant?.lastname || '',
        ageCategory: participant?.ageCategory || '',
        bib: participant?.bib || '',
        displayName: participant?.displayName || '',
        gender: participant?.gender || '',
        row: participant?.row || '',
        status: participant?.status || '',
        nationality: participant?.nationality || '',
        nationalId: participant?.nationalId || '',
        options: participant?.options || {},
      }
      setFormState(resetState)
    }
  }, [participant, setFormState])

  const handleSave = useCallback(async () => {
    try {
      await updateParticipant({
        variables: {
          record: {
            ...formState,
            _id: participant._id,
          },
        },
      })
      showSuccess('Participant updated successfully!', 'Update Successful')
      setIsEditMode(false)
      // Call onUpdate to refetch and update participant data
      if (onUpdate) {
        await onUpdate()
      }
    } catch (err) {
      console.error('Error updating participant:', err)
      const errorMessage =
        err.message ||
        err.graphQLErrors?.[0]?.message ||
        'Failed to update participant. Please try again.'
      showError(errorMessage, 'Update Failed')
    }
  }, [formState, participant, updateParticipant, showSuccess, showError, onUpdate])

  const handleClose = useCallback(() => {
    if (isEditMode) {
      handleCancel()
    }
    onClose()
  }, [isEditMode, handleCancel, onClose])

  // Show action buttons only when in edit mode
  const showActionButtons = isEditMode

  if (!participant) return null

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: { xs: '95vh', sm: '90vh', md: '85vh' },
          display: 'flex',
          flexDirection: 'column',
        },
      }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'center' },
          p: { xs: 2, sm: 2.5, md: 3 },
          borderBottom: (theme) =>
            `1px solid ${theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.12)'}`,
        }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="h5"
            component="h2"
            sx={{
              fontWeight: 700,
              color: 'text.primary',
              fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' },
              mb: { xs: 0.5, sm: 0.75 },
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}>
            {fullName}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            {formState.bib && (
              <Chip
                label={`BIB: ${formState.bib}`}
                size="small"
                sx={{
                  fontSize: { xs: '0.75rem', sm: '0.8125rem' },
                  height: { xs: 24, sm: 28 },
                }}
              />
            )}
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                fontSize: { xs: '0.75rem', sm: '0.8125rem', md: '0.875rem' },
              }}>
              Participant Details
            </Typography>
          </Stack>
        </Box>
        <IconButton
          onClick={handleClose}
          sx={{
            color: 'text.secondary',
            ml: 1,
            '&:hover': {
              color: 'text.primary',
              backgroundColor: (theme) =>
                theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.04)' : 'rgba(255, 255, 255, 0.08)',
            },
          }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent
        sx={{
          p: { xs: 2, sm: 2.5, md: 3 },
          flex: 1,
          overflow: 'auto',
          '&.MuiDialogContent-root': {
            paddingTop: { xs: 2, sm: 2.5, md: 3 },
          },
        }}>
        <Stack spacing={{ xs: 4, sm: 4.5, md: 5 }}>
          {/* User Profile & Comparison Section */}
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: 'text.primary',
                fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' },
                mb: { xs: 2, sm: 2.5, md: 3 },
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}>
              <PersonIcon sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' } }} />
              User Profile & Comparison
            </Typography>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2, sm: 2.5, md: 3 },
                borderRadius: 2,
                backgroundColor: (theme) =>
                  theme.palette.mode === 'light' ? 'rgba(249, 250, 251, 0.5)' : 'rgba(18, 18, 18, 0.5)',
              }}>
              {/* Images Container */}
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={{ xs: 2, sm: 2.5, md: 3 }}
                sx={{ mb: { xs: 2, sm: 2.5, md: 3 }, justifyContent: 'center', alignItems: 'flex-start' }}>
                <Box sx={{ flex: { xs: '1 1 100%', sm: '0 0 auto' } }}>
                  <ImageCard
                    title="Image Submitted by User"
                    imageUrl={participant.photo}
                    alt="User submitted photo"
                  />
                </Box>
                <Box sx={{ flex: { xs: '1 1 100%', sm: '0 0 auto' } }}>
                  <ImageCard
                    title="Reference Image (for Registration)"
                    imageUrl={participant.referenceImage}
                    alt="Reference image"
                  />
                </Box>
              </Stack>
              {/* Face Match Confidence Bar */}
              <FaceMatchConfidenceBar confidence={participant.faceMatchConfidence || '98.7'} />
            </Paper>
          </Box>

          {/* Information Details Section */}
          <Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: { xs: 2, sm: 2.5, md: 3 },
                flexDirection: { xs: 'column', sm: 'row' },
                gap: { xs: 1, sm: 0 },
              }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: 'text.primary',
                  fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' },
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}>
                <InfoIcon sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' } }} />
                Information Details
              </Typography>
              {!isEditMode && (
                <Button
                  startIcon={<EditIcon />}
                  onClick={handleEdit}
                  variant="outlined"
                  size="small"
                  sx={{
                    borderRadius: 1.5,
                    textTransform: 'none',
                    width: { xs: '100%', sm: 'auto' },
                  }}>
                  Edit
                </Button>
              )}
            </Box>

            {isEditMode ? (
              <ParticipantEditForm
                formState={formState}
                setFormState={setFormState}
                participant={participant}
                genderList={genderList}
              />
            ) : (
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={{ xs: 2, sm: 2.5, md: 3 }}
                sx={{ alignItems: 'flex-start' }}>
                <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 50%' } }}>
                  <Stack spacing={{ xs: 1.5, sm: 2, md: 2.5 }}>
                    <InfoField label="BIB" value={formState.bib} icon={ConfirmationNumberIcon} monospace />
                    <InfoField label="First Name" value={formState.firstname} icon={PersonIcon} />
                    <InfoField label="Last Name" value={formState.lastname} icon={PersonIcon} />
                    <InfoField label="Display Name" value={formState.displayName} icon={PersonIcon} />
                    <InfoField label="Gender" value={formState.gender} icon={WcIcon} />
                    <InfoField label="Age Category" value={formState.ageCategory} icon={CalendarTodayIcon} />
                  </Stack>
                </Box>
                <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 50%' } }}>
                  <Stack spacing={{ xs: 1.5, sm: 2, md: 2.5 }}>
                    <InfoField label="Row" value={formState.row} icon={InfoIcon} />
                    <InfoField label="Status" value={formState.status} icon={CheckCircleIcon} />
                    <InfoField label="Nationality" value={formState.nationality} icon={FlagIcon} />
                    <InfoField label="National ID" value={formState.nationalId} icon={BadgeIcon} />
                    <Box>
                      <Stack direction="row" spacing={0.75} alignItems="center" mb={{ xs: 0.75, sm: 1 }}>
                        <CheckCircleIcon
                          sx={{
                            fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' },
                            color: 'text.secondary',
                          }}
                        />
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 500,
                            color: 'text.secondary',
                            fontSize: { xs: '0.75rem', sm: '0.8125rem', md: '0.875rem' },
                          }}>
                          Check-in Status
                        </Typography>
                      </Stack>
                      <Paper
                        elevation={0}
                        sx={{
                          p: { xs: 1.25, sm: 1.5, md: 1.75 },
                          borderRadius: 1.5,
                          backgroundColor: (theme) =>
                            theme.palette.mode === 'light'
                              ? 'rgba(249, 250, 251, 0.5)'
                              : 'rgba(18, 18, 18, 0.5)',
                        }}>
                        <Chip
                          label={participant.lastActivity?.status || 'Not Checked In'}
                          size="small"
                          sx={{
                            backgroundColor: (theme) =>
                              participant.lastActivity?.status === 'checkin'
                                ? theme.palette.mode === 'light'
                                  ? 'rgba(76, 175, 80, 0.1)'
                                  : 'rgba(76, 175, 80, 0.2)'
                                : 'transparent',
                            color: (theme) =>
                              participant.lastActivity?.status === 'checkin'
                                ? theme.palette.mode === 'light'
                                  ? 'rgba(76, 175, 80, 0.9)'
                                  : 'rgba(129, 199, 132, 0.9)'
                                : 'text.secondary',
                            fontWeight: 500,
                            fontSize: { xs: '0.8125rem', sm: '0.875rem', md: '0.9375rem' },
                          }}
                        />
                      </Paper>
                    </Box>
                  </Stack>
                </Box>
              </Stack>
            )}
          </Box>

          {/* Verification History Section */}
          {verificationHistory.length > 0 && (
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: 'text.primary',
                  fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' },
                  mb: { xs: 2, sm: 2.5, md: 3 },
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}>
                <AccessTimeIcon sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' } }} />
                Verification History
              </Typography>
              <Stack spacing={{ xs: 1.5, sm: 2, md: 2.5 }}>
                {verificationHistory.map((activity, index) => (
                  <VerificationHistoryItem key={index} activity={activity} theme={theme} />
                ))}
              </Stack>
            </Box>
          )}
        </Stack>
      </DialogContent>

      <DialogActions
        sx={{
          p: { xs: 2, sm: 2.5, md: 3 },
          borderTop: (theme) =>
            `1px solid ${theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.12)'}`,
          flexDirection: { xs: 'column-reverse', sm: 'row' },
          justifyContent: 'flex-end',
          gap: { xs: 1, sm: 1.25, md: 1.5 },
        }}>
        {showActionButtons ? (
          <>
            <Button
              onClick={handleCancel}
              variant="outlined"
              startIcon={<CancelIcon />}
              fullWidth={isMobile}
              disabled={updating}
              sx={{
                borderRadius: 1.5,
                fontSize: { xs: '0.8125rem', sm: '0.875rem', md: '0.9375rem' },
                fontWeight: 500,
                textTransform: 'none',
                px: { xs: 2, sm: 2.5, md: 3 },
                py: { xs: 1.25, sm: 1.375, md: 1.5 },
              }}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              fullWidth={isMobile}
              disabled={updating}
              sx={{
                borderRadius: 1.5,
                fontSize: { xs: '0.8125rem', sm: '0.875rem', md: '0.9375rem' },
                fontWeight: 600,
                textTransform: 'none',
                px: { xs: 2, sm: 2.5, md: 3 },
                py: { xs: 1.25, sm: 1.375, md: 1.5 },
              }}>
              {updating ? 'Saving...' : 'Save Changes'}
            </Button>
          </>
        ) : (
          <Button
            onClick={handleClose}
            variant="outlined"
            fullWidth={isMobile}
            sx={{
              borderRadius: 1.5,
              fontSize: { xs: '0.8125rem', sm: '0.875rem', md: '0.9375rem' },
              fontWeight: 500,
              textTransform: 'none',
              px: { xs: 2, sm: 2.5, md: 3 },
              py: { xs: 1.25, sm: 1.375, md: 1.5 },
            }}>
            Close
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}

export default React.memo(ParticipantDetailsDialog)
